import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { getWeekdayLabel, getMonthDayLabel } from "../utils/formatDateLabels";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";
import { calculateDayVolume, formatVolumeLoad } from "../utils/volumeLoad";

const buildExerciseLookup = () => Object.fromEntries(getAllExercisesCatalog().map((item) => [item.id, item]));

const truncateToTwoWords = (text) => {
    if (typeof text !== "string") {
        return text;
    }

    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) {
        return text;
    }

    return `${words.slice(0, 2).join(" ")}...`;
};

function collectExercisesForDate({ date, exerciseLog, exerciseMap, t }) {
    return Object.entries(exerciseLog[date] || {}).map(([exerciseId]) => {
        const definition = exerciseMap[exerciseId];
        const fallbackName = t("home.unknownExercise", { id: exerciseId });

        return {
            id: exerciseId,
            date,
            name: definition ? getLocalizedExerciseName(definition, t) : fallbackName,
        };
    });
}

export default function ExerciseProgressTable({
    dates = [],
    exerciseLog = {},
    workoutNotes = {},
    onOpenNoteModal,
}) {
    const { t, locale, localizePath } = useI18n();
    const exerciseMap = useMemo(buildExerciseLookup, []);

    const rows = useMemo(
        () =>
            dates.map((date) => {
                const note = workoutNotes?.[date];
                const hasNote = typeof note === "string" && note.trim().length > 0;
                return {
                    key: date,
                    weekday: getWeekdayLabel(date, locale),
                    monthDay: getMonthDayLabel(date, locale),
                    dayVolume: calculateDayVolume(exerciseLog?.[date]),
                    exercises: collectExercisesForDate({ date, exerciseLog, exerciseMap, t }),
                    note: hasNote ? note : "",
                    hasNote,
                };
            }),
        [dates, locale, exerciseLog, exerciseMap, workoutNotes, t]
    );

    return (
        <table className="compactExerciseTable progressByDateTable" aria-label={t("home.exerciseHistoryTable")}>
            <thead>
            <tr>
                <th>{t("home.date")}</th>
                <th>{t("home.exercise")}</th>
            </tr>
            </thead>
            <tbody>
            {rows.map(({ key, weekday, monthDay, dayVolume, exercises, note, hasNote }) => (
                <tr key={key}>
                    <td className="progressDateCell">
                        <div className="dateHeaderWeekday">{weekday}</div>
                        <div className="dateHeaderDate">{monthDay}</div>
                        {dayVolume > 0 && (
                            <div className="dateVolumeBadge">
                                {t("home.volumeLoad")}: {formatVolumeLoad(dayVolume, locale)}
                            </div>
                        )}
                        <div className="dateNoteBlock">
                            {hasNote ? (
                                <button
                                    type="button"
                                    className="noteBadgeButton noteBadgeButton--hasNote"
                                    onClick={() => onOpenNoteModal && onOpenNoteModal({ date: key, note })}
                                    data-date={key}
                                    title={note}
                                    aria-label={t("home.editNoteForDate", { date: key })}
                                >
                                    <span className="noteBadgeIcon" aria-hidden="true">📝</span>
                                    <span className="noteBadgeText">{truncateToTwoWords(note)}</span>
                                    <span className="noteBadgeActionLabel">{t("home.editNote")}</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="noteBadgeButton noteBadgeButton--empty"
                                    onClick={() => onOpenNoteModal && onOpenNoteModal({ date: key, note: "" })}
                                    data-date={key}
                                    aria-label={t("home.addNoteForDate", { date: key })}
                                    title={t("home.addNote")}
                                >
                                    <span className="noteAddPlus" aria-hidden="true">+</span>
                                    <span className="noteBadgeActionLabel">{t("home.addNote")}</span>
                                </button>
                            )}
                        </div>
                    </td>
                    <td>
                        <div className="exerciseGroup">
                            {exercises.map(({ id, date, name }) => (
                                <Link
                                    key={`${id}-${date}`}
                                    to={localizePath(`/LogExercise/${id}/${date}`)}
                                    className="exerciseLink"
                                >
                                    <span>{truncateToTwoWords(name)}</span>
                                </Link>
                            ))}
                        </div>
                    </td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}
